import {
  Box,
  Button,
  Stack,
  Textarea,
  Loader,
  Text,
  Card,
  SimpleGrid,
  ScrollArea,
  Divider,
  Table,
  Paper,
  ActionIcon,
  Center,
} from '@mantine/core';
import { Encounter, Patient } from '@medplum/fhirtypes';
import { useTranslation } from 'react-i18next';
import { IconPlus, IconEye } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { StatusBadge } from '@/components/medplum-react/StatusBadge/StatusBadge';
import { formatDate } from '@/pages/ainigma/lims/utils/common-helpers';
import { OrderUtils } from '@/pages/ainigma/lims/utils/order-table.utils';
import { useDateDisplay } from '@/hooks/useDateDisplay';
import { useEncounterOrders } from './hooks/useEncounterOrders';
import { useEncounterRecommendations } from './hooks/useEncounterRecommendations';
import { useSaveRecommendation } from './hooks/useSaveRecommendation';
import classes from './RecommendationsGeneticTestingTab.module.css';
import { resolveId } from '@medplum/core';

type RecommendationsGeneticTestingTabProps = {
  patient: Patient;
  encounter: Encounter;
  readonly?: boolean;
};

export const RecommendationsGeneticTestingTab = ({
  patient,
  encounter,
  readonly = false,
}: RecommendationsGeneticTestingTabProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation(['patient-pages']);

  // Use date display hook for formatting timestamps
  const formatTime = useDateDisplay(
    {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    },
    ''
  );

  // Use date display hook for formatting dates in previous recommendations
  const formatRecommendationDate = useDateDisplay(
    {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    },
    ''
  );

  // Use the custom hook for orders
  const { orders, loading: ordersLoading } = useEncounterOrders(
    patient,
    encounter
  );

  // Use the custom hook for recommendations
  const {
    recommendation,
    initialRecommendation,
    previousRecommendations,
    loading,
    updateRecommendation,
    updateInitialRecommendation,
  } = useEncounterRecommendations(patient, encounter);

  // Use the custom hook for saving recommendations
  const { saveRecommendation, saving, lastSaved, saveError, retrySave } =
    useSaveRecommendation(
      patient,
      encounter,
      recommendation,
      initialRecommendation,
      updateInitialRecommendation
    );

  // Auto-save on recommendation change
  const handleRecommendationChange = (value: string) => {
    updateRecommendation(value);
    saveRecommendation();
  };

  // Render save status indicator
  const renderSaveStatus = () => {
    if (saveError) {
      return (
        <Text
          size="xs"
          c="red"
          className={classes.saveStatusError}
          onClick={retrySave}
        >
          ⚠ {t('examination.content.saveError', { ns: 'patient-pages' })}
        </Text>
      );
    }

    if (saving) {
      return (
        <Text size="xs" c="dimmed">
          ⏳ {t('examination.content.saving', { ns: 'patient-pages' })}
        </Text>
      );
    }

    if (lastSaved) {
      return (
        <Text size="xs" c="green">
          ✓ {t('examination.content.saved', { ns: 'patient-pages' })}{' '}
          {formatTime(lastSaved)}
        </Text>
      );
    }

    return null;
  };

  const handleCreateOrder = () => {
    const returnUrl = `/Patient/${patient.id}/current-encounter?tab=recommendations`;
    const encounterId = encounter.id;
    const patientId = patient.id;

    // Find the referring doctor from encounter participants
    const referringDoctor = encounter.participant?.find(
      (p) => p.type?.[0]?.coding?.[0]?.code === 'REF'
    );
    const referrerId = resolveId(referringDoctor?.individual);

    // Build URL with patient and referrer pre-filled
    const params = new URLSearchParams({
      returnUrl,
      encounterId: encounterId || '',
      patientId: patientId || '',
    });

    if (referrerId) {
      params.append('referrerId', referrerId);
    }

    navigate(`/lims/new?${params.toString()}`);
  };

  if (loading) {
    return (
      <Box p="lg">
        <Stack gap="lg" align="center">
          <Loader size="lg" />
          <Text>
            {t('examination.content.loading', { ns: 'patient-pages' })}
          </Text>
        </Stack>
      </Box>
    );
  }

  return (
    <Box>
      <Stack gap="lg">
        {/* Recommendations Section */}
        <SimpleGrid cols={2} spacing="md">
          {/* Left side: Current recommendations (editable textarea with auto-save) */}
          <Stack gap="xs" h="100%">
            <Box className={classes.recommendationHeader}>
              <Text size="sm" fw={500}>
                {t('examination.content.recommendation', {
                  ns: 'patient-pages',
                })}
              </Text>
              {!readonly && renderSaveStatus()}
            </Box>
            {readonly ? (
              <Text>
                {recommendation || (
                  <Text c="dimmed" fs="italic">
                    {t('examination.content.noRecommendation', {
                      ns: 'patient-pages',
                      defaultValue: 'No recorded recommendation',
                    })}
                  </Text>
                )}
              </Text>
            ) : (
              <Textarea
                name="recommendation"
                value={recommendation}
                onChange={(e) =>
                  handleRecommendationChange(e.currentTarget.value)
                }
                minRows={6}
                classNames={{
                  root: classes.textareaContainer,
                  wrapper: classes.textareaWrapper,
                  input: classes.textareaInput,
                }}
              />
            )}
          </Stack>

          {/* Right side: Previous recommendations (read-only card) */}
          <Stack gap="xs">
            <Text size="sm" fw={500}>
              {t('examination.content.previousRecommendations', {
                ns: 'patient-pages',
              })}
            </Text>
            <Card
              shadow="sm"
              p="lg"
              radius="md"
              withBorder
              bg="blue.0"
              mah={250}
              className={classes.previousRecommendationsCard}
            >
              <ScrollArea className={classes.scrollArea}>
                {previousRecommendations.length > 0 ? (
                  <Stack gap="md">
                    {previousRecommendations.map((rec, index) => (
                      <Box key={index}>
                        <Text size="xs" c="dimmed" mb="xs">
                          {formatRecommendationDate(rec.date)}
                        </Text>
                        <Text
                          size="sm"
                          c="dimmed"
                          className={classes.recommendationText}
                        >
                          {rec.text}
                        </Text>
                        {index < previousRecommendations.length - 1 && (
                          <Divider my="sm" />
                        )}
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Text size="sm" c="dimmed">
                    {t('examination.content.noPreviousRecommendations', {
                      ns: 'patient-pages',
                    })}
                  </Text>
                )}
              </ScrollArea>
            </Card>
          </Stack>
        </SimpleGrid>

        <Divider my={!readonly ? 'lg' : undefined} />

        {/* Orders Section */}
        <Box>
          <Text size="sm" fw={600} mb="xs">
            {t('examination.content.encounterOrders', {
              ns: 'patient-pages',
              defaultValue: 'Genetic Test Orders',
            })}
          </Text>
          <Paper shadow="sm" withBorder>
            {ordersLoading ? (
              <Center p="lg">
                <Loader size="sm" />
              </Center>
            ) : orders.length === 0 ? (
              <Center p="lg">
                <Text size="sm" c="dimmed">
                  {t('examination.content.noOrders', {
                    ns: 'patient-pages',
                    defaultValue: 'No orders for this encounter',
                  })}
                </Text>
              </Center>
            ) : (
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>
                      {t('examination.content.orderId', {
                        ns: 'patient-pages',
                        defaultValue: 'Order ID',
                      })}
                    </Table.Th>
                    <Table.Th>
                      {t('examination.content.testType', {
                        ns: 'patient-pages',
                        defaultValue: 'Test Type',
                      })}
                    </Table.Th>
                    <Table.Th>
                      {t('examination.content.status', {
                        ns: 'patient-pages',
                        defaultValue: 'Status',
                      })}
                    </Table.Th>
                    <Table.Th>
                      {t('examination.content.date', {
                        ns: 'patient-pages',
                        defaultValue: 'Date',
                      })}
                    </Table.Th>
                    <Table.Th>
                      {t('examination.content.actions', {
                        ns: 'patient-pages',
                        defaultValue: 'Actions',
                      })}
                    </Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {orders.map((order) => (
                    <Table.Tr
                      key={order.id}
                      onClick={() =>
                        order.id && navigate(`/lims/order/${order.id}`)
                      }
                      className={classes.clickableRow}
                    >
                      <Table.Td>
                        <Text fw={500}>{OrderUtils.getOrderId(order)}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">
                          {OrderUtils.getTestName(
                            order,
                            t('examination.content.unknownTest', {
                              ns: 'patient-pages',
                              defaultValue: 'Unknown Test',
                            })
                          )}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <StatusBadge
                          status={OrderUtils.getSubStateStatus(order)}
                          label={t(OrderUtils.getSubStateName(order) as any, {
                            ns: 'lims',
                          })}
                        />
                      </Table.Td>
                      <Table.Td>{formatDate(order.authoredOn)}</Table.Td>
                      <Table.Td>
                        <ActionIcon
                          variant="subtle"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (order.id) {
                              navigate(`/lims/order/${order.id}`);
                            }
                          }}
                        >
                          <IconEye size={16} />
                        </ActionIcon>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Paper>
        </Box>

        {/* Create Order Button */}
        {!readonly && (
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={handleCreateOrder}
            variant="light"
            fullWidth
          >
            {t('examination.content.createOrder', {
              ns: 'patient-pages',
            })}
          </Button>
        )}
      </Stack>
    </Box>
  );
};
